import React from "react";

import { Form } from "formik";
import Autocomplete from "react-google-autocomplete";
import { isValidPhoneNumber } from "react-phone-number-input";
import { Building, ChevronDown, Mail, MapPin, Phone, User } from "lucide-react";

import {
  CircularProgress,
  FormControl,
  FormHelperText,
  Autocomplete as MuiAutocomplete,
  TextField,
} from "@mui/material";

import { GOOGLE_API_KEY } from "../helpers/constants";
import { useContactPopover } from "../common/useContactPopover";
import { Button } from "../common/customDesignedComponents/button";
import CustomPhoneNumberInput from "../common/CustomPhoneNumberInput";
import { SEARCH_PROPERTY_ADDRESS_FORMAT } from "../helpers/constants";
import { Checkbox } from "../common/customDesignedComponents/checkbox";
import { FormInput } from "../common/customDesignedComponents/FormInput";
import HubspotCookieBreakerInput from "../common/HubspotCookieBreakerInput";

import "../../tailwind.css";
import "./PropertyRiskManagementFormTailwind.scss";

interface PropertyRiskManagementFormTailwindProps {
  values: any;
  setFieldValue: (field: string, value: any) => void;
  setFieldTouched: (
    field: string,
    touched?: boolean,
    shouldValidate?: boolean,
  ) => void;
  touched: any;
  errors: any;
  isSubmitting: boolean;
  formRef: React.RefObject<HTMLFormElement>;
  value: string;
  setValue: (value: string) => void;
  handleChange: (address: any) => void;
  checked1: boolean;
  onCheckedAcknowledge: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmitClick: boolean;
  checkTerms: boolean;
  isAdmin: boolean;
  host_path: string | null;
  Btn_Text: string;
  isBackButton: boolean;
  setIsRiskFormVisible: (visible: boolean) => void;
  acknowledgeMsg: React.ReactNode;
  open: boolean;
  setOpen: (open: boolean) => void;
  options: any[];
  getCountyList: (value: string) => void;
  setOptions: (options: any[]) => void;
  statesLoader: boolean;
  hideSearchTypeDropdown?: boolean;
  isCompactLayout?: boolean;
}

const PropertyRiskManagementFormTailwind: React.FC<
  PropertyRiskManagementFormTailwindProps
> = ({
  values,
  setFieldValue,
  setFieldTouched,
  touched,
  errors,
  formRef,
  value,
  setValue,
  handleChange,
  checked1,
  onCheckedAcknowledge,
  onSubmitClick,
  checkTerms,
  isAdmin,
  host_path,
  Btn_Text,
  isBackButton,
  setIsRiskFormVisible,
  acknowledgeMsg,
  open,
  setOpen,
  options,
  getCountyList,
  setOptions,
  statesLoader,
  hideSearchTypeDropdown = false,
  isCompactLayout = false,
}) => {
  const { handleClick, ContactPopover } = useContactPopover();
  const [countyQuery, setCountyQuery] = React.useState("");

  const nameField = (
    <FormInput
      type="text"
      id="userName"
      name="name"
      placeholder={isAdmin ? "Client's Name" : "Your Name"}
      icon={User}
      value={values.name || ""}
      onChange={(e) => {
        setFieldValue("name", e.target.value);
      }}
      onBlur={() => {
        setFieldTouched("name", true);
      }}
      touched={touched.name}
      error={errors.name}
    />
  );

  const emailField = (
    <FormInput
      type="email"
      id="emailAddress"
      name="email"
      placeholder={isAdmin ? "Client's Email" : "Your Email"}
      icon={Mail}
      value={values.email || ""}
      onChange={(e) => {
        setFieldValue("email", e.target.value);
      }}
      onBlur={() => {
        setFieldTouched("email", true);
      }}
      touched={touched.email}
      error={errors.email}
    />
  );

  const unitField = (
    <FormInput
      type="text"
      id="unitNumber"
      name="unit_no"
      placeholder={isCompactLayout ? "Unit (optional)" : "Unit Number (optional)"}
      icon={Building}
      value={values.unit_no || ""}
      onChange={(e) => setFieldValue("unit_no", e.target.value)}
      showError={false}
    />
  );

  const phoneField = (
    <div
      className={`tailwind-phone-input ${
        isCompactLayout ? "compact_phone_input" : ""
      }`}
    >
      <div className="relative form-input-wrapper">
        {/* At half width the country flag already marks the field */}
        {!isCompactLayout && (
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground z-20 pointer-events-none form-input-icon" />
        )}
        <div className="phone-input-container">
          <CustomPhoneNumberInput
            name="phone"
            values={values}
            touched={touched}
            setFieldValue={setFieldValue}
            errors={errors}
            placeholder="Mobile Phone"
            showError={false}
            otherProps={{
              id: "phoneNumber",
              onChange: (event) => {
                setFieldValue("phone", event);
                if (!event) {
                  setFieldValue("phone", "");
                }
              },
            }}
          />
        </div>
      </div>
      {((values?.phone && !isValidPhoneNumber(values?.phone)) ||
        (touched.phone && errors.phone)) && (
        <p className="text-sm text-red-500 mt-1 tailwind_error_msg">
          {touched.phone && errors.phone
            ? errors.phone
            : "Please enter a valid phone number"}
        </p>
      )}
    </div>
  );

  // Compact layout puts parcel number and county side by side
  const wrapParcelFields = (fields: React.ReactNode) =>
    isCompactLayout ? (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">{fields}</div>
    ) : (
      fields
    );

  return (
    <div className="property_risk_form_tw">
      <Form
        id="property-risk-form"
        ref={formRef}
        className={isCompactLayout ? "space-y-2" : "space-y-4"}
        noValidate
      >
        {/* Hubspot hidden input for cookie breaker */}
        <HubspotCookieBreakerInput />

        {isCompactLayout ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {nameField}
              {emailField}
            </div>
            {/* Unit sits beside phone here instead of under the address */}
            <div
              className={`grid grid-cols-1 gap-2 ${
                value === "address" ? "sm:grid-cols-2" : ""
              }`}
            >
              {phoneField}
              {value === "address" && unitField}
            </div>
          </>
        ) : (
          <>
            {nameField}
            {emailField}
            {phoneField}
          </>
        )}

        {/* Search Type Toggle */}
        {!hideSearchTypeDropdown && (
          <div className="relative">
            {isCompactLayout && (
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground z-10 pointer-events-none" />
            )}
            <select
              value={value}
              id="searchType"
              onChange={(e) => setValue(e.target.value)}
              className={`tailwind-select-dropdown w-full h-10 rounded-md border border-input bg-background ${
                isCompactLayout ? "pl-10" : "px-3"
              } pr-10 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 appearance-none cursor-pointer ${
                value ? "has-value" : ""
              }`}
            >
              <option value="address">Search by Address</option>
              <option value="parcel">
                {isCompactLayout ? "Search by Parcel Number" : "Search by Parcel"}
              </option>
            </select>
            <ChevronDown className="tailwind-select-arrow absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        )}

        {/* Address Search */}
        {value === "address" && (
          <>
            <div>
              <div className="relative form-input-wrapper">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground z-10 pointer-events-none form-input-icon" />
                <Autocomplete
                  className={`w-full h-10 rounded-md border ${
                    touched.address && Boolean(errors.address)
                      ? "border-red-500"
                      : "border-input"
                  } bg-background pl-10 pr-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2`}
                  name="property"
                  id="propertyAddress"
                  apiKey={GOOGLE_API_KEY}
                  onPlaceSelected={(address) => {
                    handleChange(address);
                  }}
                  onBlur={(event) => {
                    const addressValue = (event.target as HTMLInputElement)
                      .value;
                    setFieldValue("address", addressValue);
                    setFieldTouched("address", true);
                  }}
                  onKeyDown={(event) =>
                    event.key === "Enter" ? event.preventDefault() : null
                  }
                  placeholder={SEARCH_PROPERTY_ADDRESS_FORMAT}
                  options={{
                    types: ["address"],
                    componentRestrictions: { country: "us" },
                  }}
                />
              </div>
              {touched.address && Boolean(errors.address) && (
                <p className="text-sm text-red-500 mt-1 tailwind_error_msg">
                  {errors.address}
                </p>
              )}
            </div>

            {!isCompactLayout && unitField}
          </>
        )}

        {/* Parcel Search */}
        {value === "parcel" &&
          wrapParcelFields(
          <>
            <FormInput
              type="text"
              id="parcelNumber"
              name="parcel_number"
              placeholder="Parcel Number"
              value={values.parcel_number || ""}
              onChange={(e) => {
                setFieldValue("parcel_number", e.target.value);
              }}
              onBlur={() => {
                setFieldTouched("parcel_number", true);
              }}
              touched={touched.parcel_number}
              error={errors.parcel_number}
            />

            <div
              className={`relative tailwind-county-autocomplete ${
                touched.county_code && Boolean(errors.county_code)
                  ? "has-error"
                  : ""
              }`}
            >
              <FormControl
                fullWidth
                error={touched.county_code && Boolean(errors.county_code)}
              >
                <MuiAutocomplete
                  className={`${
                    touched.county_code && Boolean(errors.county_code)
                      ? "error_border"
                      : ""
                  }`}
                  id="county"
                  disableClearable
                  // Switching to parcel pre-fetches counties in the background;
                  // only show the list (and its loader) once the user opens it
                  open={open}
                  onOpen={() => setOpen(true)}
                  onClose={() => {
                    if (!statesLoader) {
                      setOpen(false);
                    }
                  }}
                  onChange={(event, newValue) => {
                    setFieldValue("county_code", String(newValue?.id));
                    setFieldValue("county_name", newValue?.county);
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option.county === value.county
                  }
                  getOptionLabel={(option) =>
                    option.county || values.county_code
                  }
                  options={options}
                  loading={statesLoader}
                  onInputChange={(event, inputValue) =>
                    setCountyQuery(inputValue)
                  }
                  // Same wording as the legacy form; the API needs 2+ characters
                  noOptionsText={
                    countyQuery.trim().length >= 2
                      ? "No counties found"
                      : "Type at least 2 characters to search counties"
                  }
                  renderInput={(params) => (
                    <TextField
                      onChange={(event) => {
                        getCountyList(event.target.value);
                        if (event.target.value.trim().length >= 2) {
                          setOpen(true);
                        }
                      }}
                      onBlur={() => {
                        setFieldTouched("county_code", true);
                      }}
                      placeholder="County"
                      {...params}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {open && statesLoader ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                />
                {touched.county_code && Boolean(errors.county_code) && (
                  <FormHelperText>
                    {touched.county_code && errors.county_code}
                  </FormHelperText>
                )}
              </FormControl>
            </div>
          </>,
          )}

        {/* Acknowledgment Checkbox */}
        {!isAdmin && (
        <div className="flex items-start space-x-3">
          <Checkbox
            id="acknowledge"
            checked={checked1}
            onCheckedChange={(checked) => {
              const syntheticEvent = {
                target: { checked: checked as boolean },
              } as React.ChangeEvent<HTMLInputElement>;
              onCheckedAcknowledge(syntheticEvent);
            }}
          />
          <label
            htmlFor="acknowledge"
            className={`${
              isCompactLayout ? "text-[11px] leading-snug" : "text-sm text-sm-xs leading-tight"
            } text-start text-muted-foreground cursor-pointer`}
          >
            {acknowledgeMsg ||
              "I acknowledge that an EquityProtect Fraud Specialist may contact me regarding my property risk assessment. I agree to receive calls and text messages related to my property assessment, fraud alerts, and protection services. Msg & data rates may apply."}
          </label>
        </div>
        )}

        {/* Error Message */}
        {!isAdmin && onSubmitClick && (!checkTerms || !checked1) && (
          <p className="text-sm text-red-500 fw-600 tailwind_error_msg">
            You must agree to the terms and conditions to proceed.
          </p>
        )}

        {/* Submit Button */}
        {!isAdmin && (
          <Button
            type="submit"
            size="lg"
            className={`w-full bg-green-500 hover:bg-green-600 text-white ${
              isCompactLayout ? "h-10 text-sm tracking-wide" : "text-lg"
            }`}
          >
            {host_path?.includes("Equity-Guard")
              ? "Get Free Title Monitoring"
              : Btn_Text}
          </Button>
        )}

        {/* Back Button */}
        {isBackButton && (
          <div className="text-center w-full">
            <Button
              type="button"
              variant="ghost"
              className="text-center m-0 text-primary"
              onClick={() => setIsRiskFormVisible(false)}
            >
              Back to Login form
            </Button>
          </div>
        )}

        {/* Admin Submit Button */}
        {isAdmin && (
          <Button
            type="submit"
            size="lg"
            className="w-full admin-hub-action-btn mt-2"
          >
            Run Risk Assessment
          </Button>
        )}

        {!isAdmin && !isCompactLayout && (
        <p className="text-center text-sm text-muted-foreground">
          Having trouble?{" "}
          <button
            type="button"
            onClick={handleClick}
            className="text-secondary hover:underline bg-transparent border-none p-0 cursor-pointer"
          >
            Contact support
          </button>
        </p>
        )}
      </Form>
      <ContactPopover />
    </div>
  );
};

export default PropertyRiskManagementFormTailwind;
